/**
 * useCustomers Hook
 * ahooks data fetching hook for customer list with filters, pagination, and sorting
 */

import { useMemo } from 'react';
import { useRequest, useAntdTable, clearCache } from 'ahooks';
import type { Options } from 'ahooks/lib/useRequest/src/types';
import { customerApi } from '@/services/api/customer.api';
import { useCustomerStore, useCustomerQueryParams } from '@/stores/customerStore';
import type {
  CustomerListResponse,
  CustomerDetailResponse,
  CustomerLocationsResponse,
  CustomerCheckExistsResponse,
  CustomerListParams,
  CustomerSortField,
  CustomerLocationListParams,
  CustomerContactListParams,
  CustomerLocationResponse,
  CustomerContactDetailResponse,
} from '@/types/api/customer';
import type { ListResponse } from '@/types/api/common';
import type { CustomerContactResponse } from '@/types/api/customer';
import { getCacheKey } from '@/utils/storeFirstData';
import { trackCacheKey, clearCacheWhere } from '@/utils/cacheKeyRegistry';

/**
 * Helper to invalidate all customer-related caches
 * Cache key format: /api/customers?{params}
 */
const invalidateCustomerListCaches = () => {
  // Exact-key clear of every tracked list key (the old predicate form was a
  // silent no-op; see cacheKeyRegistry.ts)
  clearCacheWhere((key) => key.startsWith('/api/customers?'));
  // Static shared dropdown-options key (useCustomerOptions, useParentCompanies,
  // ProductFormWizard) — not registry-tracked, so clear it exactly.
  clearCache('/api/customers-options');
};

/**
 * Helper to invalidate a specific customer's detail cache
 * Cache key format: /api/customers/${customerId}
 */
const invalidateCustomerDetailCache = (customerId: number) => {
  clearCache(`/api/customers/${customerId}`);
};

/**
 * Helper to invalidate both list and detail caches for a customer
 */
const invalidateCustomerCaches = (customerId: number) => {
  invalidateCustomerDetailCache(customerId);
  invalidateCustomerListCaches();
};

/**
 * Helper to invalidate customer location caches
 * Cache key format: /api/customers/${customerId}/locations
 */
export const invalidateCustomerLocationCaches = (customerId: number) => {
  clearCacheWhere((key) => key.includes(`/api/customers/${customerId}/locations`));
};

/**
 * Helper to invalidate customer contact caches
 * Cache key format: /api/customers/${customerId}/contacts
 */
export const invalidateCustomerContactCaches = (customerId: number) => {
  clearCacheWhere((key) => key.includes(`/api/customers/${customerId}/contacts`));
};

/**
 * Hook to fetch customer list with automatic state management
 * Syncs with Zustand store for filters, pagination, and sorting
 */
export function useCustomers(config?: Options<CustomerListResponse, []>) {
  // Get query params from store
  const params = useCustomerQueryParams();
  const { setCustomers, setPagination, setLoading, setError } = useCustomerStore();

  // Fetch customers with ahooks
  const { data, error, loading, refresh } = useRequest(() => customerApi.list(params), {
    refreshDeps: [
      params.page,
      params.pageSize,
      params.search,
      params.categoryIds,
      params.isActive,
      params.sortBy,
      params.sortOrder,
    ],
    cacheKey: trackCacheKey(`/api/customers?${JSON.stringify(params)}`),
    staleTime: 5000, // 5 seconds (reduced for better responsiveness)
    ...config,
    onSuccess: (data) => {
      setCustomers(data.data);
      setPagination(data.pagination);
      setLoading(false);
      setError(null);
      config?.onSuccess?.(data, []);
    },
    onError: (err) => {
      setLoading(false);
      setError(err.message || 'Failed to fetch customers');
      config?.onError?.(err, []);
    },
  });

  return {
    customers: data?.data ?? [],
    pagination: data?.pagination,
    error,
    isLoading: loading,
    mutate: refresh,
    refresh,
  };
}

/**
 * Hook using useAntdTable for seamless Ant Design Table integration
 * Returns tableProps that can be spread directly onto <Table>
 *
 * @param filters - External filters (search, categoryIds, isActive) from store or local state
 */
export function useCustomersTable(filters: {
  search?: string;
  categoryIds?: number[];
  country?: string;
  isActive?: boolean | null;
}) {
  const { setCustomers, setPagination } = useCustomerStore();

  // Map column field names to API sortBy values (numeric per API spec)
  // API sortBy: 1=customerId, 2=customerName, 3=customerCode, 4=localTaxId, 5=address, 6=phone, 7=isActive
  const fieldToSortBy: Record<string, CustomerSortField> = {
    customerId: 1,
    customerName: 2,
    customerCode: 3,
    localTaxId: 4,
    address: 5,
    phone: 6,
    isActive: 7,
  };

  // Convert filters to API format
  const getApiParams = (): Partial<CustomerListParams> => {
    let isActiveParam: number | undefined;
    if (filters.isActive === true) {
      isActiveParam = 1;
    } else if (filters.isActive === false) {
      isActiveParam = 2;
    } else {
      isActiveParam = 3; // null means all
    }

    return {
      search: filters.search || undefined,
      categoryIds:
        filters.categoryIds && filters.categoryIds.length > 0
          ? filters.categoryIds.join(',')
          : undefined,
      country: filters.country || undefined,
      isActive: isActiveParam,
    };
  };

  const { tableProps, refresh, loading, error, params } = useAntdTable(
    async ({ current, pageSize, sorter }) => {
      // Map sorter to API format (numeric per API spec)
      // Default: sort by customerId descending (newest first)
      let sortBy: CustomerSortField = 1; // customerId
      let sortOrder: 0 | 1 = 1; // descend

      if (sorter && 'field' in sorter && sorter.order) {
        sortBy = fieldToSortBy[sorter.field as string] ?? 1;
        sortOrder = sorter.order === 'descend' ? 1 : 0;
      }

      const params: CustomerListParams = {
        page: current,
        pageSize,
        ...getApiParams(),
        sortBy,
        sortOrder,
      };

      const response = await customerApi.list(params);

      // Sync with Zustand store
      setCustomers(response.data);
      setPagination(response.pagination);

      return {
        total: response.pagination.total,
        list: response.data,
      };
    },
    {
      defaultPageSize: 10,
      refreshDeps: [
        filters.search,
        JSON.stringify(filters.categoryIds),
        filters.country,
        filters.isActive,
      ],
    }
  );

  return {
    tableProps,
    refresh: () => {
      // Clear caches and refresh
      invalidateCustomerListCaches();
      refresh();
    },
    isLoading: loading || params.length === 0,
    error,
  };
}

/**
 * Hook to fetch customer detail by ID
 */
export function useCustomerDetail(
  customerId: number | null,
  config?: Options<CustomerDetailResponse, []>
) {
  const { data, error, loading, mutate, run, refresh } = useRequest(
    () => customerApi.getById(customerId!),
    {
      ready: !!customerId,
      cacheKey: customerId ? `/api/customers/${customerId}` : undefined,
      staleTime: 5000, // 5 seconds (reduced for better responsiveness)
      ...config,
    }
  );

  // Consider it loading if:
  // 1. loading is true, OR
  // 2. customerId is provided but data hasn't arrived yet AND no error occurred
  const isLoading = loading || (!!customerId && !data && !error);

  return {
    customer: data,
    error,
    isLoading,
    mutate, // ahooks mutate - for direct data manipulation
    run, // run forces a new request, bypassing cache
    refresh, // ahooks refresh - re-runs with same params
  };
}

/**
 * Hook to fetch customer locations by customer ID
 */
export function useCustomerLocations(
  customerId: number | null,
  params?: CustomerLocationListParams,
  config?: Options<CustomerLocationsResponse, []>
) {
  const { data, error, loading, refresh, run, mutate } = useRequest(
    () => customerApi.getLocations(customerId!, params),
    {
      ready: !!customerId,
      refreshDeps: [customerId, JSON.stringify(params)],
      // NOTE: Caching disabled for customer locations to ensure fresh data after mutations
      // The SWR pattern in ahooks causes issues where clearCache() + run() still returns stale data
      // See: https://ahooks.js.org/hooks/use-request/cache/
      ...config,
      // Handle 404 errors gracefully - locations API may not exist yet on backend
      onError: (err) => {
        // Log error but don't propagate - return empty array instead
        console.warn(
          `[useCustomerLocations] Failed to fetch locations for customer ${customerId}:`,
          err.message
        );
        config?.onError?.(err, []);
      },
    }
  );

  return {
    locations: data?.data ?? [],
    error,
    isLoading: loading,
    refresh,
    /** Force a fresh API request */
    refetch: run,
    /** Optimistically update local data */
    mutate,
  };
}

/**
 * Hook to fetch customer contacts by customer ID
 */
export function useCustomerContacts(
  customerId: number | null,
  params?: CustomerContactListParams,
  config?: Options<ListResponse<CustomerContactResponse>, []>
) {
  const { data, error, loading, refresh } = useRequest(
    () => customerApi.getContacts(customerId!, params),
    {
      ready: !!customerId,
      refreshDeps: [customerId, JSON.stringify(params)],
      cacheKey: customerId
        ? trackCacheKey(getCacheKey(`/api/customers/${customerId}/contacts`, params))
        : undefined,
      staleTime: 5000,
      ...config,
      onError: (err) => {
        console.warn(
          `[useCustomerContacts] Failed to fetch contacts for customer ${customerId}:`,
          err.message
        );
        config?.onError?.(err, []);
      },
    }
  );

  return {
    contacts: data?.data ?? [],
    error,
    isLoading: loading,
    refresh,
  };
}

/**
 * Hook to fetch a single customer location by ID
 * Used for edit modal to get raw location data
 */
export function useCustomerLocationDetail(
  customerId: number | null,
  locationId: number | null,
  config?: Options<CustomerLocationResponse, []>
) {
  const { data, error, loading, refresh } = useRequest(
    () => customerApi.getLocationById(customerId!, locationId!),
    {
      ready: !!customerId && !!locationId,
      refreshDeps: [customerId, locationId],
      cacheKey:
        customerId && locationId
          ? trackCacheKey(`/api/customers/${customerId}/locations/${locationId}`)
          : undefined,
      staleTime: 5000, // 5 seconds
      ...config,
    }
  );

  return {
    location: data ?? null,
    error,
    isLoading: loading,
    refresh,
  };
}

/**
 * Hook to fetch a single customer contact by ID
 * Used for edit modal to get raw address data
 */
export function useCustomerContactDetail(
  customerId: number | null,
  contactId: number | null,
  config?: Options<CustomerContactDetailResponse, []>
) {
  const { data, error, loading, refresh } = useRequest(
    () => customerApi.getContactById(customerId!, contactId!),
    {
      ready: !!customerId && !!contactId,
      refreshDeps: [customerId, contactId],
      cacheKey:
        customerId && contactId
          ? trackCacheKey(`/api/customers/${customerId}/contacts/${contactId}`)
          : undefined,
      staleTime: 5000, // 5 seconds
      ...config,
    }
  );

  return {
    contact: data ?? null,
    error,
    isLoading: loading,
    refresh,
  };
}

/**
 * Hook to check if customer exists (for validation)
 * Uses countryId and localTaxId to check customer existence
 */
export function useCheckCustomerExists(
  countryId: number | null,
  localTaxId: string | null,
  config?: Options<CustomerCheckExistsResponse, []>
) {
  const { data, error, loading } = useRequest(
    () => customerApi.checkExists({ countryId: countryId!, localTaxId: localTaxId! }),
    {
      ready: !!(countryId && localTaxId),
      // Re-fetch when countryId or localTaxId changes
      refreshDeps: [countryId, localTaxId],
      cacheKey:
        countryId && localTaxId
          ? `/api/customers/check-exists/${countryId}/${localTaxId}`
          : undefined,
      cacheTime: 1000,
      ...config,
    }
  );

  return {
    exists: data?.exists ?? false,
    companyExists: data?.companyExists ?? false,
    existingCustomerId: data?.customerId,
    existingCompany: data?.company,
    message: data?.message,
    isLoading: loading,
    error,
  };
}

/**
 * Hook for customer mutations (create, update, toggle status)
 * All mutations invalidate ahooks cache to ensure UI stays in sync
 */
export function useCustomerMutations() {
  const { updateCustomer, removeCustomer } = useCustomerStore();

  const createCustomer = async (data: Parameters<typeof customerApi.create>[0]) => {
    const response = await customerApi.create(data);
    // Invalidate list cache to show new customer
    invalidateCustomerListCaches();
    return response;
  };

  const quickCreateCustomer = async (data: Parameters<typeof customerApi.quickCreate>[0]) => {
    const response = await customerApi.quickCreate(data);
    // Invalidate list cache to show new customer
    invalidateCustomerListCaches();
    return response;
  };

  const updateCustomerData = async (
    customerId: number,
    data: Parameters<typeof customerApi.update>[1]
  ) => {
    const response = await customerApi.update(customerId, data);
    updateCustomer(customerId, response);
    // Invalidate both detail and list caches
    invalidateCustomerCaches(customerId);
    return response;
  };

  const toggleCustomerStatus = async (customerId: number, isActive: boolean) => {
    const response = await customerApi.toggleStatus(customerId, isActive);
    // Update store with new status
    useCustomerStore.getState().updateCustomerInList(customerId, { isActive: response.isActive });
    // Invalidate caches
    invalidateCustomerCaches(customerId);
    return response;
  };

  const deleteCustomer = async (customerId: number) => {
    // Note: API doesn't have delete endpoint, but we can prepare for it
    removeCustomer(customerId);
    // Invalidate both detail and list caches
    invalidateCustomerCaches(customerId);
  };

  return {
    createCustomer,
    quickCreateCustomer,
    updateCustomer: updateCustomerData,
    toggleStatus: toggleCustomerStatus,
    deleteCustomer,
  };
}

/**
 * Hook for customer location mutations (create, update, toggle status, set default)
 * All mutations invalidate ahooks cache to ensure UI stays in sync
 *
 * Follows the same pattern as useLocationMutations for vendors
 */
export function useCustomerLocationMutations(customerId: number) {
  const createLocation = async (data: Parameters<typeof customerApi.createLocation>[1]) => {
    const response = await customerApi.createLocation(customerId, data);
    // Invalidate cache to refresh list
    invalidateCustomerLocationCaches(customerId);
    return response;
  };

  const updateLocation = async (
    locationId: number,
    data: Parameters<typeof customerApi.updateLocation>[2]
  ) => {
    const response = await customerApi.updateLocation(customerId, locationId, data);
    // Invalidate cache to refresh list
    invalidateCustomerLocationCaches(customerId);
    return response;
  };

  const toggleLocationStatus = async (locationId: number, isActive: boolean) => {
    const response = await customerApi.toggleLocationStatus(customerId, locationId, isActive);
    // Invalidate cache to refresh list with updated status
    invalidateCustomerLocationCaches(customerId);
    return response;
  };

  const setDefaultShipping = async (locationId: number) => {
    const response = await customerApi.setDefaultLocation(customerId, locationId, {
      isDefaultShipping: true,
    });
    // Invalidate cache to refresh list with updated default
    invalidateCustomerLocationCaches(customerId);
    return response;
  };

  const setDefaultBilling = async (locationId: number) => {
    const response = await customerApi.setDefaultLocation(customerId, locationId, {
      isDefaultBilling: true,
    });
    // Invalidate cache to refresh list with updated default
    invalidateCustomerLocationCaches(customerId);
    return response;
  };

  return {
    createLocation,
    updateLocation,
    toggleStatus: toggleLocationStatus,
    setDefaultShipping,
    setDefaultBilling,
  };
}

/**
 * Hook to fetch all customers for parent company dropdown selection
 * Returns a simplified list for Select options
 *
 * Uses shared cache key '/api/customers-options' - same as useParentCompanies
 * Filtering by excludeCustomerId is done in memory, not via separate API calls
 */
export function useCustomerOptions(excludeCustomerId?: number) {
  const { data, error, loading } = useRequest(
    () => customerApi.list({ isActive: 1, pageSize: -1 }), // Fetch all active customers (API spec: -1 = all)
    {
      cacheKey: '/api/customers-options',
      staleTime: 60000, // 1 minute cache
    }
  );

  // Filter in memory - this runs on cached data, no extra API call
  const customers = useMemo(() => {
    if (!data?.data) return [];
    return data.data.filter((c) => c.customerId !== excludeCustomerId);
  }, [data, excludeCustomerId]);

  return {
    customers,
    isLoading: loading,
    error,
  };
}
